import React, { useState, useEffect, useRef } from "react";
import MessageList from "../../roleplay/MessageList";
import ChatInput from "../../roleplay/ChatInput";
import {
	Message,
	RoleplaySettingsProps,
	defaultRoleplaySettings,
} from "../../../types";
import { useLiveTutor } from "../../../hooks/LiveTutorProvider";
import { useAzure } from "../../../hooks/useAzure";

interface RoleplayProps {
	scenario: string;
	llm_instructions: string;
	voice_id: string;
	image: { src: string; alt: string; url?: string };
	profile_image: { src: string; alt: string };
}

const Roleplay: React.FC<RoleplayProps> = ({
	scenario,
	llm_instructions,
	image,
	profile_image,
	voice_id,
}) => {
	const [unblurredSet, setUnblurredSet] = useState<Set<number>>(new Set([0]));
	const [isPlayingIndex, setIsPlayingIndex] = useState(-1);
	const [settings, setSettings] = useState<RoleplaySettingsProps>(
		defaultRoleplaySettings
	);
	const {
		callbackRef,
		messageCallbackRef,
		userMessageCallbackRef,
		actionHandler,
		handleSpeak,
		exerciseInfoRef,
		handleChat,
	} = useLiveTutor();
	const { stopTextAsync } = useAzure();

	const [messages, setMessages] = useState<Message[]>([
		{
			role: "system",
			content: `You are in a roleplay scenario with a user (who is practicing English).
You can end the conversation by using the continue() action.`,
			scenario: scenario,
		},
	]);
	const messagesRef = useRef(messages);
	useEffect(() => {
		exerciseInfoRef.current["messages"] = messages;
		messagesRef.current = messages;
	}, [messages]);

	useEffect(() => {
		actionHandler.current["roleplay"] = ({ text }: { text: string }) => {
			// remove quote if text is wrapped in quotes
			if (text.startsWith('"') && text.endsWith('"')) {
				text = text.slice(1, -1);
			}

			// add to messages
			const newMessages = [...messagesRef.current];
			newMessages.push({
				role: "assistant",
				content: text,
			});
			setMessages(newMessages);
			console.log("roleplay received", text, voice_id);
			handleSpeak(text, "roleplay", voice_id);
		};

		messageCallbackRef.current = (response: string) => {};

		userMessageCallbackRef.current = (response: string) => {
			// add to messages
			const newMessages = [...messagesRef.current];
			newMessages.push({
				role: "user",
				content: response,
			});
			setMessages(newMessages);
		};

		callbackRef.current = (response: string) => {
			setIsPlayingIndex(-1);
		};

		handleChat("Read the instruction/question.", false);

		return () => {
			setIsPlayingIndex(-1);
			stopTextAsync();
		};
	}, []);

	return (
		<div className="flex flex-col mb-24">
			<div className="mb-6">
				<img
					src={image.src || image.url}
					alt={image.alt}
					className="w-full h-64 object-cover rounded-lg shadow-md"
				/>
			</div>

			<MessageList
				hasFeedback={false}
				scenario={{ messages, voiceId: voice_id }}
				settings={settings}
				profile={profile_image}
				unblurredSet={unblurredSet}
				setUnblurredSet={setUnblurredSet}
				isPlayingIndex={isPlayingIndex}
				setIsPlayingIndex={setIsPlayingIndex}
			/>
		</div>
	);
};

export default Roleplay;
